import { UploadDocumentRequest, UploadDocumentResponse } from 'src/easyclaim.api.types/document';

import { getAppParameters } from '../AppParameters';
import { Language } from '../easyclaim.api.client.typescript/src/index';
import { getEasyClaimAuthorizationHeaders } from '../easyclaim.app.configuration/ciapiAuthorization';
import { getEasyClaimEnv } from '../easyclaim.app.configuration/env';

const REQUEST_TIMEOUT_MS = 45_000;

export interface UploadDocumentParams {
	caseId: string;
	fileContent: string;
	fileName: string;
	documentType: string;
	documentId?: string;
	signal?: AbortSignal;
	/** Overrides {@link REQUEST_TIMEOUT_MS}; primarily intended for tests. */
	timeoutMs?: number;
}

export class DocumentUploadError extends Error {
	constructor(
		public readonly responseStatus: number,
		public readonly responseStatusText: string
	) {
		super(`Document upload failed with status ${responseStatus}`);
		this.name = 'DocumentUploadError';
	}
}

export class DocumentDeleteError extends Error {
	constructor(
		public readonly responseStatus: number,
		public readonly responseStatusText: string
	) {
		super(`Document deletion failed with status ${responseStatus}`);
		this.name = 'DocumentDeleteError';
	}
}

function determineResultLanguage(): string {
	const lang = getAppParameters().lang.toUpperCase();

	return lang in Language ? lang : Language.EN;
}

async function fetchWithConnectionGuard(
	url: string | URL,
	init: RequestInit,
	signal: AbortSignal | undefined,
	timeoutMs: number = REQUEST_TIMEOUT_MS
): Promise<Response> {
	if (typeof navigator !== 'undefined' && navigator.onLine === false) {
		throw new Error('Request failed: no network connection');
	}

	const timeoutController = new AbortController();
	const onExternalAbort = () => timeoutController.abort();

	signal?.addEventListener('abort', onExternalAbort);

	const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);

	try {
		return await fetch(url, { ...init, signal: timeoutController.signal });
	} catch (error) {
		if (signal?.aborted) {
			throw error;
		}

		if (timeoutController.signal.aborted) {
			throw new Error('Request failed: network request timed out');
		}

		throw error;
	} finally {
		clearTimeout(timeoutId);
		signal?.removeEventListener('abort', onExternalAbort);
	}
}

export async function uploadDocument({
	caseId,
	fileContent,
	fileName,
	documentType,
	documentId = crypto.randomUUID(),
	signal,
	timeoutMs,
}: UploadDocumentParams): Promise<UploadDocumentResponse> {
	const requestBody: UploadDocumentRequest = {
		caseId,
		documentId,
		documentType,
		fileContent,
		fileName,
		resultLanguage: determineResultLanguage(),
	};

	const { basePath, apiKey } = getEasyClaimEnv();
	const url = new URL(`${basePath}/api/v2/document`);

	url.searchParams.set('caseId', caseId);

	const response = await fetchWithConnectionGuard(
		url,
		{
			method: 'PUT',
			headers: {
				accept: '*/*',
				'content-type': 'application/json',
				'x-apikey': apiKey,
				...getEasyClaimAuthorizationHeaders(),
			},
			body: JSON.stringify(requestBody),
		},
		signal,
		timeoutMs
	);

	if (response.status === 422) {
		try {
			return (await response.json()) as UploadDocumentResponse;
		} catch {
			throw new DocumentUploadError(response.status, response.statusText);
		}
	}

	if (!response.ok) {
		throw new DocumentUploadError(response.status, response.statusText);
	}

	return (await response.json()) as UploadDocumentResponse;
}

export interface DeleteDocumentParams {
	caseId: string;
	documentId: string;
	signal?: AbortSignal;
	/** Overrides {@link REQUEST_TIMEOUT_MS}; primarily intended for tests. */
	timeoutMs?: number;
}

export async function deleteDocument({
	caseId,
	documentId,
	signal,
	timeoutMs,
}: DeleteDocumentParams): Promise<void> {
	const { basePath, apiKey } = getEasyClaimEnv();
	const url = new URL(`${basePath}/api/v2/document`);

	url.searchParams.set('caseId', caseId);
	url.searchParams.set('documentId', documentId);

	const response = await fetchWithConnectionGuard(
		url,
		{
			method: 'DELETE',
			headers: {
				accept: '*/*',
				'x-apikey': apiKey,
				...getEasyClaimAuthorizationHeaders(),
			},
		},
		signal,
		timeoutMs
	);

	if (!response.ok) {
		throw new DocumentDeleteError(response.status, response.statusText);
	}
}
